/**
 * FullStory Browser API v2 helpers.
 * @see https://developer.fullstory.com/browser/fullcapture/capture-data/
 *
 * Stop:    FS.shutdown()
 * Resume:  FS.restart()
 *
 * Note:
 * - shutdown() stops recording for the current page.
 * - It does NOT persist across a full page reload.
 */

import { getPersistedCancerDetectClicked } from '@hooks/useCancerDetectUser';

interface FullStoryApi {
  shutdown?: () => void;
  restart?: () => void;
  setPage?: (page: { pageName: string }) => void;
}

declare global {
  interface Window {
    FS?: FullStoryApi;
  }
}

/** Stops FullStory recording (safe to call multiple times). */
export const shutdownFullStory = (): void => {
  window.FS?.shutdown?.();
};

/** Restarts FullStory recording (safe to call multiple times). */
export const restartFullStory = (): void => {
  window.FS?.restart?.();
};

/**
 * Must match the `name` passed to `persist()` in useContextStore.ts.
 */
const CONTEXT_STORE_SESSION_KEY = 'context-store';

/**
 * Synchronously reads sessionStorage for a persisted PRO/patient context,
 * bypassing zustand's (async) rehydration entirely. Used to shut down
 * FullStory immediately after the snippet defines `window.FS` in main.tsx —
 * before React mounts and before FullStory's own script finishes loading —
 * so a hard reload of a PRO session is never captured, even briefly.
 */
export const hasPersistedPatientContext = (): boolean => {
  try {
    const raw = sessionStorage.getItem(CONTEXT_STORE_SESSION_KEY);
    return Boolean(raw && JSON.parse(raw)?.state?.patientOrg);
  } catch {
    return false;
  }
};

/**
 * Synchronously reads the persisted CancerDetect UI flag (localStorage) so a
 * hard reload of an active CD session can shut down FullStory before React
 * mounts — same reload race as PRO / patient context.
 */
export const hasPersistedCancerDetectContext = (): boolean =>
  getPersistedCancerDetectClicked();
